import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
  PlaceOutlined as PlaceIcon,
} from '@mui/icons-material';
import { useAdminOrder } from '../../hooks/admin/useAdminOrders';
import api from '../../services/api';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ghostBtnSx,
  dangerBtnSx,
  ledgerInputSx,
  ledgerSelectSx,
  editorialDialogTitleSx,
  fieldLabelSx,
} from '../../styles/ledger';
import {
  LedgerPage,
  KPIStrip,
  KPI,
  LedgerCard,
  SectionHeader,
  StatusDot,
  MonoTag,
  ledgerTableHeadSx,
  ledgerRowSx,
} from '../../components/admin/LedgerPrimitives';

/** Address parts arrive either as plain strings or as {name: …} relation objects. */
const zoneName = (z: any): string => {
  if (!z) return '';
  if (typeof z === 'string') return z;
  return z.name || z.label || '';
};

/** Carrier event locations can be strings or structured objects. */
const formatEventLocation = (loc: any): string => {
  if (!loc) return '';
  if (typeof loc === 'string') return loc;
  return [loc.city, loc.stateOrProvince, loc.state, loc.countryCode, loc.country]
    .map(zoneName)
    .filter(Boolean)
    .join(', ');
};

interface TrackingEvent {
  status: string;
  location: any;
  timestamp: string;
  description: string;
}

const statusTone = (s: string): 'active' | 'inactive' | 'warning' | 'info' | 'neutral' => {
  if (s === 'delivered' || s === 'completed' || s === 'paid') return 'active';
  if (s === 'cancelled' || s === 'failed') return 'inactive';
  if (s === 'pending' || s === 'draft') return 'warning';
  if (
    s === 'processing' || s === 'shipped' || s === 'in_transit' ||
    s === 'out_for_delivery' || s === 'scheduled_for_pickup' || s === 'ready'
  ) return 'info';
  return 'neutral';
};

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orderId = id || '';

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusType, setStatusType] = useState<'order' | 'shipment' | 'payment'>('order');
  const [editFormData, setEditFormData] = useState({
    tracking_number: '',
    notes: '',
    expected_delivery_date: '',
    reference_number: '',
  });
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState<null | 'commercial' | 'proforma'>(null);

  const { order, isLoading, error, updateOrder, updateOrderStatus, updateShipmentStatus, updatePaymentStatus } = useAdminOrder(orderId);

  const hasTrackingNumber = !!(order?.tracking_number || order?.trackingNumber);
  const {
    data: tracking,
    isLoading: isTrackingLoading,
    refetch: refetchTracking,
  } = useQuery({
    queryKey: ['admin', 'order-tracking', orderId],
    queryFn: async () => {
      const { data } = await api.get(`/orders/${orderId}/tracking`);
      return data?.data;
    },
    enabled: !!orderId && hasTrackingNumber,
    retry: false, // 404 = carrier has nothing yet, don't hammer
  });
  const [isRefreshingTracking, setIsRefreshingTracking] = useState(false);

  const handleRefreshTracking = async () => {
    setIsRefreshingTracking(true);
    try {
      await api.post(`/orders/${orderId}/track`);
      await refetchTracking();
    } catch (err) {
      console.error('Tracking refresh failed:', err);
    } finally {
      setIsRefreshingTracking(false);
    }
  };

  React.useEffect(() => {
    if (order) {
      setEditFormData({
        tracking_number: order.tracking_number || '',
        notes: (order as any).notes || '',
        expected_delivery_date: (order as any).expected_delivery_date || '',
        reference_number: (order as any).reference_number || '',
      });
    }
  }, [order]);

  const handleDownloadInvoice = async (type: 'commercial' | 'proforma') => {
    if (!orderId) return;
    setIsDownloadingInvoice(type);
    try {
      const response = await api.get(`/admin/orders/${orderId}/invoice`, {
        params: { type },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const orderRef = order?.order_number || order?.orderNumber || orderId;
      link.download = `${type}-invoice-${orderRef}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      alert(`Failed to download ${type} invoice. Please try again.`);
    } finally {
      setIsDownloadingInvoice(null);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedStatus) return;
    try {
      if (statusType === 'order') await updateOrderStatus(selectedStatus as any);
      else if (statusType === 'shipment') await updateShipmentStatus(selectedStatus as any);
      else if (statusType === 'payment') await updatePaymentStatus(selectedStatus as any);
      setStatusDialogOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-IL', { style: 'currency', currency: order?.currency || 'ILS', minimumFractionDigits: 2 }).format(amount);

  const formatDate = (s: string) => (s ? new Date(s).toLocaleDateString('en-CA') + ' ' + new Date(s).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');

  if (isLoading) {
    return (
      <LedgerPage>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={28} sx={{ color: INK.accent }} />
        </Box>
      </LedgerPage>
    );
  }

  if (error || !order) {
    return (
      <LedgerPage>
        <Alert severity="error" sx={{ borderRadius: 0, fontFamily: FONTS.sans, mb: 2 }}>
          Error loading order data.
        </Alert>
        <Button onClick={() => navigate('/admin/orders')} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>
          Back to orders
        </Button>
      </LedgerPage>
    );
  }

  const orderNumber = order.order_number || order.orderNumber || `Order ${order.id}`;
  const trackingNumber = order.tracking_number || order.trackingNumber;
  const totalPrice = order.total_price || order.totalPrice || 0;
  const createdAt = order.created_at || order.createdAt || '';
  const shippedAt = order.shipped_at || order.shippedAt || '';
  const deliveredAt = order.delivered_at || order.deliveredAt || '';
  const fromAddress = order.from_address || order.fromAddress;
  const toAddress = order.to_address || order.toAddress;
  const parcels = order.parcels || [];
  const orderStatus = order.order_status || order.status || 'processing';
  const paymentStatus = order.payment_status || 'pending';
  const shipmentStatus = order.shipment_status || (orderStatus === 'shipped' ? 'in_transit' : 'pending');

  const trackingInfo = tracking?.tracking_info;
  const trackingEvents: TrackingEvent[] = [...(trackingInfo?.events || [])].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  const latestEvent = trackingEvents[0];

  // Parcel items arrive as an array, or a JSON string, under several keys
  const parcelItems = (p: any): any[] => {
    const raw = p.items || p.Items || p.parcel_items || [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return [];
      }
    }
    return [];
  };

  const renderAddress = (label: string, addr: any) => (
    <Box>
      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.18em', color: INK.accent, textTransform: 'uppercase', fontWeight: 600, mb: 1.5 }}>
        {label}
      </Typography>
      {addr ? (
        <Stack spacing={0.5}>
          <Typography sx={{ fontFamily: FONTS.serif, fontSize: 20, color: INK.text, letterSpacing: '-0.01em' }}>
            {addr.contactName || addr.contact_name || '—'}
          </Typography>
          {addr.email && <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.muted }}>{addr.email}</Typography>}
          {addr.phone && <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.muted }}>{addr.phone}</Typography>}
          {addr.street && <Typography sx={{ fontSize: 13, color: INK.text, mt: 0.5 }}>{addr.street}</Typography>}
          <Typography sx={{ fontSize: 13, color: INK.muted }}>
            {[
              zoneName(addr.city) || addr.city_other,
              zoneName(addr.state),
              addr.zip || addr.postal_code,
              zoneName(addr.country),
            ]
              .filter(Boolean)
              .join(', ')}
          </Typography>
        </Stack>
      ) : (
        <Typography sx={{ fontFamily: FONTS.serif, fontSize: 18, color: INK.subtle }}>Not provided</Typography>
      )}
    </Box>
  );

  return (
    <LedgerPage eyebrowFooter={`Almaship · Order · ${orderNumber}`}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Button
          onClick={() => navigate('/admin/orders')}
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          sx={{ ...ghostBtnSx, border: 'none', color: INK.muted, px: 1, '&:hover': { bgcolor: 'transparent', color: INK.accent, borderColor: 'transparent' } }}
        >
          Back to manifest
        </Button>
        <Stack direction="row" spacing={1.5}>
          <Button startIcon={<PrintIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>Print</Button>
          <Button
            onClick={() => handleDownloadInvoice('commercial')}
            disabled={isDownloadingInvoice !== null}
            startIcon={<DescriptionIcon sx={{ fontSize: 16 }} />}
            sx={ghostBtnSx}
          >
            {isDownloadingInvoice === 'commercial' ? 'Downloading…' : 'Commercial Invoice'}
          </Button>
          <Button
            onClick={() => handleDownloadInvoice('proforma')}
            disabled={isDownloadingInvoice !== null}
            startIcon={<DescriptionIcon sx={{ fontSize: 16 }} />}
            sx={ghostBtnSx}
          >
            {isDownloadingInvoice === 'proforma' ? 'Downloading…' : 'Proforma Invoice'}
          </Button>
          <Button onClick={() => setEditDialogOpen(true)} startIcon={<EditIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>Edit</Button>
          <Button onClick={() => setDeleteDialogOpen(true)} startIcon={<DeleteIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>Delete</Button>
        </Stack>
      </Stack>

      <Box sx={{ mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Box sx={{ width: 24, height: 1, bgcolor: INK.accent }} />
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, letterSpacing: '0.18em', color: INK.accent, fontWeight: 500, textTransform: 'uppercase' }}>
            Manifest · Order
          </Typography>
        </Stack>
        <Typography sx={{ fontFamily: FONTS.serif, fontSize: { xs: 44, md: 64 }, lineHeight: 0.95, letterSpacing: '-0.02em', color: INK.text, fontWeight: 400 }}>
          {orderNumber}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.muted, letterSpacing: '0.04em' }}>
            Placed {formatDate(createdAt)}
          </Typography>
          {trackingNumber && <MonoTag>TRK · {trackingNumber}</MonoTag>}
        </Stack>
      </Box>

      <KPIStrip cols={4}>
        <KPI
          label="Order"
          value={<Typography sx={{ fontFamily: FONTS.serif, fontSize: { xs: 22, md: 28 }, letterSpacing: '-0.01em' }}>{orderStatus.replace(/_/g, ' ')}</Typography>}
          sub={`Status · ${formatDate(createdAt)}`}
        />
        <KPI
          label="Shipment"
          value={<Typography sx={{ fontFamily: FONTS.serif, fontSize: { xs: 22, md: 28 }, letterSpacing: '-0.01em' }}>{shipmentStatus.replace(/_/g, ' ')}</Typography>}
          sub={shippedAt ? `Shipped ${formatDate(shippedAt)}` : 'Not shipped yet'}
        />
        <KPI
          label="Payment"
          value={<Typography sx={{ fontFamily: FONTS.serif, fontSize: { xs: 22, md: 28 }, letterSpacing: '-0.01em' }}>{paymentStatus.replace(/_/g, ' ')}</Typography>}
          sub={`Method · ${order.payment_method || order.paymentMethod || 'N/A'}`}
        />
        <KPI label="Total" value={formatCurrency(totalPrice)} sub={deliveredAt ? `Delivered ${formatDate(deliveredAt)}` : 'open'} accent />
      </KPIStrip>

      <LedgerCard mb={3}>
        <SectionHeader
          number="01"
          eyebrow="Status"
          title="Lifecycle update"
          description="Move the order, shipment, or payment to the next state."
        />
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          {(['order', 'shipment', 'payment'] as const).map((t) => (
            <Button
              key={t}
              onClick={() => { setStatusType(t); setSelectedStatus(''); setStatusDialogOpen(true); }}
              sx={ghostBtnSx}
            >
              Update {t} status
            </Button>
          ))}
        </Stack>
        <Stack direction="row" spacing={3} sx={{ mt: 3, flexWrap: 'wrap', gap: 2 }}>
          <StatusDot label={`Order · ${orderStatus.replace(/_/g, ' ')}`} tone={statusTone(orderStatus)} />
          <StatusDot label={`Shipment · ${shipmentStatus.replace(/_/g, ' ')}`} tone={statusTone(shipmentStatus)} />
          <StatusDot label={`Payment · ${paymentStatus.replace(/_/g, ' ')}`} tone={statusTone(paymentStatus)} />
        </Stack>
      </LedgerCard>

      <LedgerCard mb={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" useFlexGap spacing={2}>
          <SectionHeader
            number="02"
            eyebrow="Tracking"
            title="Where is it?"
            description={
              hasTrackingNumber
                ? `Carrier events for ${trackingNumber}.`
                : 'No tracking number assigned yet — edit the order to add one.'
            }
          />
          {hasTrackingNumber && (
            <Button
              onClick={handleRefreshTracking}
              disabled={isRefreshingTracking}
              startIcon={
                isRefreshingTracking ? (
                  <CircularProgress size={12} sx={{ color: 'inherit' }} />
                ) : (
                  <RefreshIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={ghostBtnSx}
            >
              {isRefreshingTracking ? 'Updating…' : 'Refresh from carrier'}
            </Button>
          )}
        </Stack>

        {hasTrackingNumber &&
          (isTrackingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={22} sx={{ color: INK.accent }} />
            </Box>
          ) : !trackingInfo ? (
            <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.muted, mt: 1 }}>
              Carrier has no events for this shipment yet. Internal status:{' '}
              {(tracking?.current_status || shipmentStatus).replace(/_/g, ' ')}. Try “Refresh from carrier”.
            </Typography>
          ) : (
            <>
              <Box
                sx={{
                  border: `1px solid ${INK.hairline}`,
                  borderRadius: 0.5,
                  bgcolor: INK.surfaceTint,
                  p: 2.5,
                  mt: 1,
                  mb: 2,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <PlaceIcon sx={{ color: INK.accent, fontSize: 22 }} />
                  <Box>
                    <Typography sx={{ fontFamily: FONTS.serif, fontSize: 22, color: INK.text, letterSpacing: '-0.01em' }}>
                      {trackingInfo.status_description ||
                        (trackingInfo.status || '').replace(/_/g, ' ') ||
                        'In progress'}
                    </Typography>
                    <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.muted }}>
                      {[
                        formatEventLocation(latestEvent?.location),
                        latestEvent ? formatDate(latestEvent.timestamp) : '',
                        trackingInfo.provider ? `via ${trackingInfo.provider}` : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                    {trackingInfo.estimated_delivery_date && (
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.accent, mt: 0.25 }}>
                        ETA {formatDate(trackingInfo.estimated_delivery_date)}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>

              <Stack spacing={0}>
                {trackingEvents.map((ev, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={2}
                    sx={{
                      py: 1.25,
                      borderBottom: i === trackingEvents.length - 1 ? 'none' : `1px solid ${INK.hairlineSoft}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        mt: 0.75,
                        flexShrink: 0,
                        bgcolor: i === 0 ? INK.accent : INK.hairline,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, color: INK.text, fontWeight: i === 0 ? 600 : 400 }}>
                        {ev.description || (ev.status || '').replace(/_/g, ' ')}
                      </Typography>
                      {formatEventLocation(ev.location) && (
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle }}>
                          {formatEventLocation(ev.location)}
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.muted, whiteSpace: 'nowrap' }}>
                      {formatDate(ev.timestamp)}
                    </Typography>
                  </Stack>
                ))}
                {trackingEvents.length === 0 && (
                  <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.muted }}>
                    No carrier events yet.
                  </Typography>
                )}
              </Stack>
            </>
          ))}
      </LedgerCard>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <LedgerCard>{renderAddress('Sender', fromAddress)}</LedgerCard>
        <LedgerCard>{renderAddress('Recipient', toAddress)}</LedgerCard>
      </Box>

      <LedgerCard padded={false}>
        <Box sx={{ p: 3, borderBottom: `1px solid ${INK.hairline}` }}>
          <SectionHeader number="03" eyebrow="Manifest" title={`Parcels · ${parcels.length}`} />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={ledgerTableHeadSx}>
                <TableCell sx={{ width: 56 }}>№</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Weight</TableCell>
                <TableCell align="right">Dimensions</TableCell>
                <TableCell align="right">Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parcels.length > 0 ? (
                parcels.map((p: any, i: number) => (
                  <TableRow key={i} sx={ledgerRowSx}>
                    <TableCell>
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.muted }}>
                        {String(i + 1).padStart(2, '0')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: INK.text }}>
                      {p.description ||
                        p.itemDescription ||
                        p.packagingTypeDisplay ||
                        p.fedexPackagingType ||
                        'Package'}
                      {parcelItems(p).length > 0 && (
                        <Typography component="div" sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle, mt: 0.5 }}>
                          {parcelItems(p)
                            .map((it: any) => `${it.description || 'Item'} ×${it.quantity || 1}`)
                            .join(' · ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.text }}>
                      ×{p.quantity || 1}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.text }}>
                      {p.weight ? `${p.weight} kg` : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.text }}>
                      {p.dimensionsDisplay ||
                        (p.length && p.width && p.height ? `${p.length}×${p.width}×${p.height}` : '—')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: FONTS.mono, fontSize: 12.5, color: INK.text, fontWeight: 600 }}>
                      {(p.price ?? p.value) > 0
                        ? `${p.price ?? p.value} ${p.currency || order.currency || 'ILS'}`
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ borderBottom: 'none', py: 5, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: FONTS.serif, fontSize: 20, color: INK.subtle }}>
                      No parcels on file.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </LedgerCard>

      {/* Update status dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0.5 } }}>
        <DialogTitle sx={editorialDialogTitleSx}>Update {statusType} status</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography sx={fieldLabelSx}>New status</Typography>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ display: 'none' }}>Status</InputLabel>
              <Select value={selectedStatus} onChange={(e: SelectChangeEvent) => setSelectedStatus(e.target.value)} sx={ledgerSelectSx}>
                <MenuItem value="" disabled sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Select…</MenuItem>
                {(statusType === 'order'
                  ? ['draft', 'pending', 'processing', 'shipped', 'delivered', 'cancelled']
                  : statusType === 'shipment'
                  ? ['pending', 'scheduled_for_pickup', 'in_transit', 'out_for_delivery', 'delivered', 'cancelled']
                  : ['pending', 'paid', 'failed', 'refunded']
                ).map((s) => (
                  <MenuItem key={s} value={s} sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>
                    {s.replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setStatusDialogOpen(false)} sx={ghostBtnSx}>Cancel</Button>
          <Button onClick={handleUpdateStatus} disabled={!selectedStatus} sx={primaryBtnSx}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0.5 } }}>
        <DialogTitle sx={editorialDialogTitleSx}>Edit order</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 2 }}>
            {[
              { label: 'Tracking number', name: 'tracking_number' },
              { label: 'Reference number', name: 'reference_number' },
              { label: 'Expected delivery date', name: 'expected_delivery_date', type: 'date' },
              { label: 'Notes', name: 'notes', multiline: true, minRows: 3 },
            ].map(({ label, name, type, multiline, minRows }) => (
              <Box key={name}>
                <Typography sx={fieldLabelSx}>{label}</Typography>
                <TextField
                  fullWidth
                  size="small"
                  name={name}
                  type={type || 'text'}
                  multiline={multiline}
                  minRows={minRows}
                  value={(editFormData as any)[name]}
                  onChange={(e) => setEditFormData((p) => ({ ...p, [name]: e.target.value }))}
                  InputLabelProps={type === 'date' ? { shrink: true } : undefined}
                  sx={ledgerInputSx}
                />
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={ghostBtnSx}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                await updateOrder({ ...editFormData });
                setEditDialogOpen(false);
              } catch (err) {
                console.error(err);
              }
            }}
            sx={primaryBtnSx}
            startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 0.5 } }}>
        <DialogTitle sx={editorialDialogTitleSx}>Delete order</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONTS.sans, color: INK.muted, mt: 2 }}>
            Are you sure you want to delete this order? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={ghostBtnSx}>Cancel</Button>
          <Button
            onClick={() => { setDeleteDialogOpen(false); navigate('/admin/orders'); }}
            sx={dangerBtnSx}
            startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </LedgerPage>
  );
};

export default OrderDetailPage;
